import React, {useCallback, useEffect, useMemo, useState} from 'react'
import toaster from '../../../../Utils/toaster'
import {CommonLoading} from '../../../../Utils/commonLoading'
import {getIpiVisDashboard, type IpiVisDashboardData, type IpiVisFilters} from './_requests'
import {IpiVisFilterBar} from './IpiVisFilterBar'
import {IpiVisScorecards} from './IpiVisScorecards'
import {RankedClientList} from './RankedClientList'
import {AppointmentStatusChart} from './AppointmentStatusChart'
import {CancellationsDonut} from './CancellationsDonut'
import {TopNoShowInterpreters} from './TopNoShowInterpreters'
import {CriticalAppointmentsModal} from './CriticalAppointmentsModal'
import './IpiVisDashboard.scss'

const APP_ORIGIN = window.location.origin
const IPI_VIS_TYPES = 'os,oc,svi,tpp'
const OPEN_STATUS = '0,1,3'
const UNFILLED_STATUS = '0,1,3,4'
const CANCELLED_STATUS = '6'
const pad = (n: number) => String(n).padStart(2, '0')
const fmtDate = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
const csv = (values?: Array<string | number>) =>
  values && values.length > 0 ? values.join(',') : undefined

const defaultDateRange = (): {startDate: string; endDate: string} => {
  const now = new Date()
  return {
    startDate: fmtDate(new Date(now.getFullYear(), now.getMonth(), 1)),
    endDate: fmtDate(now),
  }
}

export const IpiVisDashboard: React.FC = () => {
  const [data, setData] = useState<IpiVisDashboardData | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)
  const initialRange = useMemo(defaultDateRange, [])
  const [filters, setFilters] = useState<IpiVisFilters>(initialRange)
  const [showCritical, setShowCritical] = useState(false)

  const load = useCallback(async (f: IpiVisFilters) => {
    setLoading(true)
    setError(false)
    try {
      const result = await getIpiVisDashboard(f)
      setData(result)
    } catch (e: any) {
      setError(true)
      toaster('error', e?.response?.data?.text || 'Failed to load dashboard.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    load(initialRange)
  }, [load, initialRange])

  const handleApply = (f: IpiVisFilters) => {
    setFilters(f)
    load(f)
  }

  const openAppointments = (opts: {status: string; accounts?: string}) => {
    const q = new URLSearchParams()
    q.set('status', opts.status)
    q.set('communicationTypes', IPI_VIS_TYPES)
    const accounts = opts.accounts ?? csv(filters.customers)
    if (accounts) q.set('accounts', accounts)
    const languages = csv(filters.languages)
    if (languages) q.set('languages', languages)
    const interpreters = csv(filters.interpreters)
    if (interpreters) q.set('interpreters', interpreters)
    if (filters.startDate) q.set('startDate', filters.startDate)
    if (filters.endDate) q.set('endDate', filters.endDate)
    window.open(`${APP_ORIGIN}/appointment/2?${q.toString()}`, '_blank')
  }

  const openProfile = (interpreterId: string) => {
    window.open(`${APP_ORIGIN}/profile/${interpreterId}`, '_blank')
  }

  return (
    <div className='ipivis-dashboard'>
      <IpiVisFilterBar
        onApply={handleApply}
        isLoading={loading}
        defaultStartDate={initialRange.startDate}
        defaultEndDate={initialRange.endDate}
      />

      {error && !data ? (
        <div className='ipivis-empty ipivis-empty--full'>
          Failed to load dashboard data. Please try again.
        </div>
      ) : !data ? null : (
        <>
          <div className='ipivis-row ipivis-row--scorecards'>
            <IpiVisScorecards
              data={data.scorecards}
              onViewOpen={() => openAppointments({status: OPEN_STATUS})}
              onViewCritical={() => setShowCritical(true)}
            />
            <RankedClientList
              variant='compact'
              title='Top 3 Clients By Unfilled Assignments'
              badgeLabel='unfilled'
              items={data.topUnfilledClients}
              onView={(id) => openAppointments({status: UNFILLED_STATUS, accounts: String(id)})}
            />
          </div>

          <AppointmentStatusChart
            data={data.statusOverTime}
            isMonthlyBucketed={data.isMonthlyBucketed}
          />

          <div className='ipivis-row ipivis-row--cancellations'>
            <CancellationsDonut data={data.cancellations} />
            <RankedClientList
              variant='stacked'
              title='Top 5 Clients By Cancelled Assignments'
              badgeLabel='canceled'
              items={data.topCancelledClients}
              onView={(id) => openAppointments({status: CANCELLED_STATUS, accounts: String(id)})}
            />
          </div>

          <TopNoShowInterpreters items={data.topNoShowInterpreters} onViewProfile={openProfile} />
        </>
      )}

      {showCritical && (
        <CriticalAppointmentsModal filters={filters} onClose={() => setShowCritical(false)} />
      )}

      {loading && <CommonLoading />}
    </div>
  )
}
